import CallModal from 'app/components/call/CallModal';
import BrowserSpecific from 'app/components/common/BrowserSpecific';
import SoundCheck from 'app/components/DeviceSetup/SoundCheck';
import MarginGroup from 'app/components/layout/MarginGroup';
import { T, tFunctionType } from 'app/components/localization';
import LoadingButtonA from 'app/components/presentation/buttons/LoadingButtonA';
import Selfie from 'app/components/rtc/Selfie';
import { DeviceType, GetDeviceManager, IsDeviceManagerBusy } from 'app/containers/rtc/DeviceManagerContext';
import { OperatingSystem } from 'app/utils/userAgent';
import React, { PureComponent } from 'react';
import DeviceSettings from 'app/components/DeviceSetup/DeviceSettings';

interface IProps {
  /**
   * Whenever this is the last screen in the current device setup
   * flow. This should always be true for video but for audio this
   * normally is false. It should be true though if video setup is
   * skipped or already active.
   */
  last: boolean;
  type: DeviceType;
}

interface IPropsWithDeps extends IProps {
  busy: boolean;
  next: () => void;
  dismiss: () => void;
}

const partsPerType = {
  [DeviceType.Audio]: (t: tFunctionType) => ({
    title: t('rtc.device-setup.check.audio.title'),
    message: t('rtc.device-setup.check.audio.message'),
    preview: <SoundCheck />,
    nextLabel: t('rtc.device-setup.check.audio.go-to-video'),
    finishLabel: t('rtc.device-setup.check.finish'),
  }),
  [DeviceType.Video]: (t: tFunctionType) => ({
    title: t('rtc.device-setup.check.video.title'),
    message: t('rtc.device-setup.check.video.message'),
    preview: <Selfie />,
    nextLabel: t('rtc.device-setup.check.finish'),
    finishLabel: t('rtc.device-setup.check.finish'),
  }),
};

class DeviceCheck extends PureComponent<IPropsWithDeps> {
  public render() {
    return (
      <T>{t => {
        const { type, busy, next, dismiss, last } = this.props;
        const { title, message, preview, nextLabel, finishLabel } = partsPerType[type](t);

        return (
          <CallModal
            buttons={
              <LoadingButtonA onClick={next} isLoading={busy}>
                {last ? finishLabel : nextLabel}
              </LoadingButtonA>
            }
            // Allow to dismiss this screen in the middle of the
            // flow but don't if it's the last step
            onClose={last ? undefined : dismiss}
            title={title}
          >
            <MarginGroup>
              <p>{message}</p>

              <BrowserSpecific>{({ os }) =>
                // Tell macOS users they need to restart their browser
                // because USB hot-plugging is hard in 2018 (?!)
                os === OperatingSystem.macOS ?
                  <p>{t('rtc.device-setup.check.mac')}</p> :
                null
              }</BrowserSpecific>

              {preview}

              <DeviceSettings type={type} />
            </MarginGroup>
          </CallModal>
        );
      }}</T>
    );
  }
}

const DeviceCheckInjectDependencies = (props: IProps) =>
  <GetDeviceManager>{(manager) => manager &&
    <IsDeviceManagerBusy>{(busy) =>
      <DeviceCheck {...props} busy={busy} next={manager.step} dismiss={manager.dismiss} />
    }</IsDeviceManagerBusy>
  }</GetDeviceManager>;

export default DeviceCheckInjectDependencies;
